import { useState, type ReactNode } from 'react'
import { Check, ChevronDown, ChevronUp, GitPullRequestArrow, Hammer, RotateCcw, Trash2, X } from 'lucide-react'
import { Button, Link } from '../../../core/index.js'
import { changeHref } from '../changes/changeText.js'
import type { FeedbackItem, FeedbackScreen, ResolveFeedbackInput } from '../../index.js'
import { FeedbackCategoryBadge, FeedbackStatusBadge, ResolutionLine, formatWhen, submitterKind, submitterLogin, submitterName } from '../../components/FeedbackStatus.js'

const REASON_MAX_LENGTH = 1000

/** True only for an address a browser would fetch over the web - never `javascript:` or `data:`, whatever the API let through. */
function isWebUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url)
    return protocol === 'http:' || protocol === 'https:'
  } catch {
    return false
  }
}

function ScreenDetails({ screen }: { screen: FeedbackScreen }) {
  const pairs: [string, ReactNode][] = [
    ['Area', screen.area === 'admin' ? 'Admin area' : 'Operator app'],
    ['Path', <span className="font-mono">{screen.path}</span>],
    ['Title', screen.title || '—'],
    [
      'URL',
      isWebUrl(screen.url) ? (
        <a href={screen.url} target="_blank" rel="noreferrer" className="font-mono text-marine hover:text-marine-dk break-all">
          {screen.url}
        </a>
      ) : (
        <span className="font-mono">{screen.url}</span>
      ),
    ],
    ['Viewport', screen.viewport],
    ['Browser', screen.userAgent],
    ['Captured', formatWhen(screen.capturedAt)],
    ...Object.entries(screen.details ?? {}).map(([key, value]): [string, ReactNode] => [key, value]),
  ]
  return (
    <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px] bg-canvas border border-marine/10 rounded p-2">
      {pairs.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="font-bold text-ink-mid">{label}</dt>
          <dd className="text-ink break-all">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

/** An icon on its own (move up, move down); the worded actions beside it are the shared `Button`. */
function IconButton({ onClick, children, label }: { onClick: () => void; children: ReactNode; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="inline-flex items-center gap-1 rounded border border-marine/30 px-2 py-1 text-[11px] font-bold text-marine hover:bg-marine-lt/40 disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-coral"
    >
      {children}
    </button>
  )
}

function DiscardForm({ onConfirm, onCancel, isPending }: { onConfirm: (reason: string) => void; onCancel: () => void; isPending: boolean }) {
  const [reason, setReason] = useState('')
  const trimmed = reason.trim()
  return (
    <div className="mt-2 border border-coral/40 rounded p-2 bg-coral-lt/40">
      <label className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">Why is this being discarded?</label>
      <textarea
        value={reason}
        maxLength={REASON_MAX_LENGTH}
        onChange={(event) => setReason(event.target.value)}
        rows={2}
        autoFocus
        placeholder="The person who asked will see this reason."
        className="w-full border border-marine/30 rounded px-2 py-1 text-xs text-ink bg-white focus:outline-none focus:ring-2 focus:ring-coral"
      />
      <div className="mt-1 flex justify-end gap-2">
        <Button icon={X} variant="secondary" size="sm" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button icon={Trash2} size="sm" onClick={() => onConfirm(trimmed)} disabled={trimmed === '' || isPending}>
          {isPending ? 'Discarding…' : 'Discard'}
        </Button>
      </div>
    </div>
  )
}

export interface FeedbackRowProps {
  item: FeedbackItem
  /** Position among open items, 1-based, when it is open. */
  position?: number
  /** Arrived since the admin's previous sign-in. */
  isNew?: boolean
  onResolve: (decision: ResolveFeedbackInput) => void
  isResolving: boolean
  /** Keyboard alternatives to dragging; absent on a resolved item. */
  onMoveUp?: () => void
  onMoveDown?: () => void
  /** Gate 1: opens a draft change for this open item; absent where it cannot be developed. */
  onDevelop?: () => void
  isDeveloping?: boolean
}

/** The change developing an item, as a chip that opens it; the shell offers the way back here. */
function ChangeChip({ changeRequestId, isOpen }: { changeRequestId: string; isOpen: boolean }) {
  return (
    <Link
      to={changeHref(changeRequestId)}
      rememberOrigin
      className="inline-flex items-center gap-1 rounded-full border border-marine/30 bg-marine-lt px-2 py-0.5 text-[10px] font-bold text-marine-dk hover:bg-marine-lt/60"
    >
      <GitPullRequestArrow aria-hidden="true" className="h-3 w-3" />
      {isOpen ? 'In development' : 'Change request'}
    </Link>
  )
}

/** One item: who asked and when, the comment, the screen they were on, and what can be done with it. */
export function FeedbackRow({ item, position, isNew = false, onResolve, isResolving, onMoveUp, onMoveDown, onDevelop, isDeveloping = false }: FeedbackRowProps) {
  const [showScreen, setShowScreen] = useState(false)
  const [isDiscarding, setIsDiscarding] = useState(false)
  const isOpen = item.status === 'open'
  const organization = item.submittedBy.organizationName

  return (
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {position !== undefined && <span className="text-[10px] font-bold text-ink-mid/70 font-mono">#{position}</span>}
        <span className="text-xs font-extrabold text-ink">{submitterName(item.submittedBy)}</span>
        <span className="text-[11px] text-ink-mid">
          {submitterKind(item.submittedBy)}
          {organization && <> · {organization}</>}
          {submitterLogin(item.submittedBy) && <> · {submitterLogin(item.submittedBy)}</>}
        </span>
        <span className="text-[11px] text-ink-mid ml-auto">{formatWhen(item.submittedAt)}</span>
        {isNew && <span className="inline-block rounded-full bg-coral px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">New</span>}
        <FeedbackCategoryBadge category={item.category} />
        <FeedbackStatusBadge status={item.status} />
      </div>
      <p className="mt-1 text-xs text-ink whitespace-pre-wrap break-words">{item.comment}</p>
      <ResolutionLine item={item} />

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {item.screen ? (
          <button type="button" onClick={() => setShowScreen((shown) => !shown)} aria-expanded={showScreen} className="inline-flex items-center gap-1 text-[11px] font-bold text-marine hover:text-marine-dk">
            {showScreen ? <ChevronUp aria-hidden="true" className="h-3.5 w-3.5" /> : <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />}
            Screen: {item.screen.title || item.screen.path}
          </button>
        ) : (
          <span className="text-[11px] text-ink-mid/70">No screen included</span>
        )}
        <span className="ml-auto flex items-center gap-1.5">
          {isOpen && onMoveUp && (
            <IconButton onClick={onMoveUp} label="Move up">
              <ChevronUp aria-hidden="true" className="h-3.5 w-3.5" />
            </IconButton>
          )}
          {isOpen && onMoveDown && (
            <IconButton onClick={onMoveDown} label="Move down">
              <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
            </IconButton>
          )}
          {item.changeRequestId ? (
            <ChangeChip changeRequestId={item.changeRequestId} isOpen={isOpen} />
          ) : (
            isOpen &&
            onDevelop && (
              <Button icon={Hammer} variant="secondary" size="sm" onClick={onDevelop} disabled={isDeveloping || isResolving}>
                {isDeveloping ? 'Starting…' : 'Develop'}
              </Button>
            )
          )}
          {isOpen ? (
            <>
              <Button icon={Check} size="sm" onClick={() => onResolve({ status: 'completed' })} disabled={isResolving || isDiscarding}>
                Complete
              </Button>
              <Button icon={Trash2} variant="secondary" size="sm" onClick={() => setIsDiscarding(true)} disabled={isResolving || isDiscarding}>
                Discard
              </Button>
            </>
          ) : (
            <Button icon={RotateCcw} variant="secondary" size="sm" onClick={() => onResolve({ status: 'open' })} disabled={isResolving}>
              Reopen
            </Button>
          )}
        </span>
      </div>

      {showScreen && item.screen && <ScreenDetails screen={item.screen} />}
      {isDiscarding && (
        <DiscardForm
          isPending={isResolving}
          onCancel={() => setIsDiscarding(false)}
          onConfirm={(reason) => {
            onResolve({ status: 'discarded', reason })
            setIsDiscarding(false)
          }}
        />
      )}
    </div>
  )
}
